import type { FC } from 'react'
import type { StoryChapter as Chapter } from '@/constants/content'

import { Gauge, Radar, Scale, Siren } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatNumber } from '@/lib/format'
import { CountUp } from '@/components/generals/count-up'

const icons = {
    siren: Siren,
    radar: Radar,
    gauge: Gauge,
    scale: Scale
}

interface StoryChapterProps {
    chapter: Chapter
}

/** One scroll stop of the 3D story. The truck animates behind it while the copy floats over. */
export const StoryChapter: FC<StoryChapterProps> = ({ chapter }) => {
    const right = chapter.side === 'right'

    return (
        <section
            id={chapter.id}
            data-chapter={chapter.index}
            aria-labelledby={`${chapter.id}-title`}
            className='relative min-h-[100svh]'
        >
            <div
                aria-hidden='true'
                className={cn(
                    'pointer-events-none absolute inset-0 bg-linear-to-t from-background via-background/80 via-40% to-transparent to-[68%] [opacity:var(--fade,1)]',
                    right
                        ? 'landscape:bg-linear-to-l landscape:from-background/90 landscape:via-background/55 landscape:via-35% landscape:to-transparent landscape:to-60%'
                        : 'landscape:bg-linear-to-r landscape:from-background/90 landscape:via-background/55 landscape:via-35% landscape:to-transparent landscape:to-60%'
                )}
            />

            <div
                className={cn(
                    'relative mx-auto flex min-h-[100svh] w-full max-w-7xl items-end px-5 pb-12 pt-[46svh] sm:px-8 landscape:items-center landscape:py-28',
                    right && 'landscape:justify-end'
                )}
            >
                <div className='chapter-content w-full max-w-[36rem] landscape:max-w-[31rem]'>
                    <p className='flex items-center gap-3 font-mono text-xs font-medium uppercase tracking-[0.22em]'>
                        <span className='text-brand'>
                            {chapter.number}
                        </span>
                        <span aria-hidden='true' className='h-px w-10 bg-brand/50' />
                        <span className='text-white/70'>
                            {chapter.eyebrow}
                        </span>
                    </p>

                    <h2
                        id={`${chapter.id}-title`}
                        className='mt-5 text-balance font-heading font-expanded text-[clamp(1.85rem,7.6vw,2.6rem)] font-extrabold leading-[1.02] tracking-tight text-white landscape:text-[clamp(2.1rem,3.3vw,3.5rem)] short:landscape:text-[clamp(1.6rem,3vw,2.2rem)]'
                    >
                        {chapter.title}
                    </h2>

                    <p className='mt-5 text-base leading-relaxed text-white/70 landscape:text-lg short:landscape:text-sm'>
                        {chapter.body}
                    </p>

                    {chapter.stats && (
                        <dl className='mt-8 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 short:landscape:mt-5'>
                            {chapter.stats.map(stat => (
                                <div key={stat.label} className='flex flex-col-reverse gap-1 bg-background/80 p-4 sm:p-5 short:landscape:p-3'>
                                    <dt className='text-xs leading-snug text-white/60'>
                                        {stat.label}
                                    </dt>
                                    <dd className='font-heading font-expanded text-xl font-bold text-white sm:text-2xl'>
                                        {stat.fixed
                                            ? `${stat.prefix ?? ''}${formatNumber(stat.value, stat.decimals)}${stat.suffix ?? ''}`
                                            : <CountUp value={stat.value} decimals={stat.decimals} prefix={stat.prefix} suffix={stat.suffix} />}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    )}

                    {chapter.features && (
                        <ul className='mt-8 grid gap-2.5 sm:grid-cols-2 short:landscape:mt-5'>
                            {chapter.features.map(feature => {
                                const Icon = icons[feature.icon]

                                return (
                                    <li key={feature.text} className='flex items-center gap-3 rounded-xl border border-white/10 bg-background/70 px-4 py-3.5 text-sm text-white/85'>
                                        <span className='flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand'>
                                            <Icon className='size-5' />
                                        </span>
                                        {feature.text}
                                    </li>
                                )
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </section>
    )
}
